import { Button, Select, TextInput } from "@mantine/core";
import {
  IconLayoutGridFilled,
  IconList,
  IconPlus,
  IconSearch,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import ProductCard from "@/components/ProductCard";
import { Spin, Table } from "antd";
import type { ColumnsType } from "antd/es/table/interface";
import { orpcClient } from "@/lib/orpc-client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function ProductManagement() {
  const { t } = useTranslation();
  // list or grid
  const [layout, setLayout] = useState("list");
  const [productData, setProductData] = useState([]);
  const [partnerData, setPartnerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(
    null,
  );
  const navigate = useNavigate();

  const handleAddProduct = () => {
    navigate("/hrMainPage/addProduct");
  };

  const handleLayoutChange = (layout) => {
    setLayout(layout);
  };

  const handleInputChange = (e) => {
    setSearchText(e.target.value);
  };

  const partnerOptions = useMemo(
    () =>
      partnerData
        .filter((partner: any) => partner?.id)
        .map((partner: any) => ({
          value: partner.id,
          label: partner?.name || partner.id,
        })),
    [partnerData],
  );

  const filteredData = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    return productData.filter((item: any) => {
      const matchesKeyword =
        !keyword || item?.name?.toLowerCase().startsWith(keyword);
      const matchesPartner =
        !selectedPartnerId || item?.partnerId === selectedPartnerId;

      return matchesKeyword && matchesPartner;
    });
  }, [productData, searchText, selectedPartnerId]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [products, partners] = await Promise.all([
          orpcClient.product.list({}),
          orpcClient.partner.list({}),
        ]);

        const partnersMap = Object.fromEntries(partners.map((p) => [p.id, p]));

        const productsWithPartners = products.map((product) => ({
          ...product,
          partner: product.partnerId ? partnersMap[product.partnerId] : null,
        }));

        setProductData(productsWithPartners);
        setPartnerData(partners);
      } catch (error) {
        console.error("Error fetching product data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const columns: ColumnsType<any> = [
    // {
    //   title: t("warehouse.productCode"),
    //   dataIndex: "code",
    //   key: "code",
    //   fixed: "left" as const,
    //   sorter: (a: any, b: any) => (a?.code || "").localeCompare(b?.code || ""),
    //   render: (text: string) => text || "",
    // },
    {
      title: t("warehouse.productName"),
      dataIndex: "name",
      key: "name",
      fixed: "left" as const,
      sorter: (a: any, b: any) => (a?.name || "").localeCompare(b?.name || ""),
      render: (text: string) => text || "",
    },
    {
      title: t("warehouse.material"),
      dataIndex: "material",
      key: "material",
      sorter: (a: any, b: any) =>
        (a?.material || "").localeCompare(b?.material || ""),
      render: (text: string) => text || "",
    },
    {
      title: t("warehouse.partner"),
      dataIndex: "partner",
      key: "partner",
      sorter: (a: any, b: any) =>
        (a?.partner?.name || "").localeCompare(b?.partner?.name || ""),
      render: (partner: any) => (partner ? <div>{partner?.name}</div> : "N/A"),
    },
    {
      title: t("warehouse.inventoryLowerLimit"),
      dataIndex: "inventoryLowerLimit",
      key: "inventoryLowerLimit",
      sorter: (a: any, b: any) =>
        (a?.inventoryLowerLimit ?? 0) - (b?.inventoryLowerLimit ?? 0),
      render: (value: any) => value ?? "",
    },
  ];

  const toggleButton = (
    value: string,
    Icon: React.ComponentType<{ size?: number | string }>,
  ) => (
    <button
      type="button"
      onClick={() => handleLayoutChange(value)}
      className={`flex h-9 w-9 items-center justify-center rounded-lg border ${
        layout === value
          ? "border-blue-200 bg-blue-50 text-blue-600"
          : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  return !loading && (!productData || productData?.length === 0) ? (
    <div className="flex min-h-full items-center justify-center p-8">
      <div className="w-[487px] rounded-xl bg-white px-16 py-20 shadow-sm">
        <div className="text-center" style={{ color: "#6F6F6F" }}>
          {t("hrMain.noProductDataYet") ||
            "您尚未新增商品管理資料！你可以試試："}
        </div>
        <div className="flex text-center">
          <Button
            variant="outline"
            color="gray"
            style={{ width: "100%", margin: "1rem" }}
            radius="md"
            onClick={handleAddProduct}
          >
            {t("hrMain.manualAdd") || "手動新增"}
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div className="min-h-full p-8">
      <div className="mx-auto max-w-6xl space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
              {t("warehouse.productManagement") || "商品管理"}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {t("warehouse.productList") || "商品清單"}
            </div>
          </div>
          <Button
            variant="filled"
            color="gray"
            radius="md"
            leftSection={<IconPlus size={16} />}
            onClick={handleAddProduct}
          >
            {t("warehouse.createProduct")}
          </Button>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-4">
            <div className="flex flex-1 items-center gap-3">
              <TextInput
                className="w-full max-w-md"
                leftSection={<IconSearch size={16} />}
                placeholder={t("warehouse.productName")}
                value={searchText}
                onChange={handleInputChange}
              />
              <Select
                className="w-56"
                placeholder={t("warehouse.filterByPartner") || "篩選合作方"}
                data={partnerOptions}
                value={selectedPartnerId}
                onChange={setSelectedPartnerId}
                clearable
                searchable
                nothingFoundMessage={
                  t("warehouse.noPartnerFound") || "查無合作方"
                }
              />
            </div>
            <div className="flex gap-2">
              {toggleButton("list", IconList)}
              {toggleButton("grid", IconLayoutGridFilled)}
            </div>
          </div>

          {layout === "list" ? (
            <div className="p-4">
              <Table
                columns={columns}
                dataSource={filteredData}
                loading={loading}
                rowKey="id"
                onRow={(record: any) => ({
                  onClick: () => navigate(`/hrMainPage/product/${record.id}`),
                  style: { cursor: "pointer" },
                })}
                scroll={{ x: "max-content", y: 600 }}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50", "100"],
                  showQuickJumper: false,
                }}
                bordered
                sticky
              />
            </div>
          ) : loading ? (
            <div className="flex justify-center p-12">
              <Spin />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredData?.map((data) => {
                return (
                  <ProductCard
                    key={data.id}
                    props={data}
                    onClick={() => navigate(`/hrMainPage/product/${data.id}`)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductManagement;
